import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Box, Divider, IconButton, Paper, Typography } from '@mui/material'
import { useOnResizeObserver } from '../../../hooks/useOnResizeObserver'
import { CollapseIcon, SortIcon } from '../../Svgs'

interface ResizablePanelHeaderProps {
  title: string
}

export const ResizablePanelHeader = (props: ResizablePanelHeaderProps) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: '5px 10px 10px 10px',
    }}
  >
    <Typography
      sx={{
        color: '#04002E',
        fontSize: '13px',
        lineHeight: 'normal',
        fontWeight: 500,
      }}
    >
      {props.title}
    </Typography>
    <Box gap='6px' sx={{ display: 'flex' }}>
      <IconButton
        disableRipple
        sx={{
          borderRadius: '4px',
          background: '#E4E4E4',
        }}
      >
        <SortIcon color='#04002E' />
      </IconButton>
      <Divider orientation='vertical' flexItem />
      <IconButton
        disableRipple
        sx={{
          borderRadius: '4px',
          background: '#E4E4E4',
        }}
      >
        <CollapseIcon color='#04002E' />
      </IconButton>
    </Box>
  </Box>
)

interface ResizablePanelProps {
  header: ReactNode
  content: ReactNode
}

export const ResizablePanel: React.FC<ResizablePanelProps> = ({
  header,
  content,
}) => {
  const [panelMinHeight, setPanelMinHeight] = useState(100)
  const [panelHeight, setPanelHeight] = useState(365)
  const [panelMaxHeight, setPanelMaxHeight] = useState(365)
  const [isResizing, setIsResizing] = useState(false)
  const startYRef = useRef(0)

  const containerRef = useRef<HTMLElement>(null)
  useOnResizeObserver(containerRef.current!, ({ height }) => {
    setPanelHeight((prevHeight) => Math.min(prevHeight, height))
    setPanelMaxHeight(height)
  })

  const headerRef = useRef<HTMLElement>(null)
  useOnResizeObserver(headerRef.current!, ({ height }) => {
    setPanelHeight((prevHeight) => Math.max(prevHeight, height))
    setPanelMinHeight(height)
  })

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsResizing(true)
    startYRef.current = e.clientY
  }, [])

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return
      const delta = startYRef.current - e.clientY
      setPanelHeight((prevHeight) =>
        Math.min(Math.max(prevHeight + delta, panelMinHeight), panelMaxHeight),
      )
      startYRef.current = e.clientY
    },
    [isResizing, panelMinHeight, panelMaxHeight],
  )

  const handleMouseUp = useCallback(() => {
    setIsResizing(false)
  }, [])

  useEffect(() => {
    const containerHeight = containerRef.current?.clientHeight
    const headerHeight = headerRef.current?.clientHeight
    if (!containerHeight || !headerHeight) {
      return
    }

    setPanelMinHeight(headerHeight)
    setPanelHeight((prevHeight) =>
      Math.min(Math.max(prevHeight, headerHeight), containerHeight),
    )
    setPanelMaxHeight(containerHeight)
  }, [])

  useEffect(() => {
    const handleMouseMoveThrottled = (e: MouseEvent) => {
      if (isResizing) {
        requestAnimationFrame(() => handleMouseMove(e))
      }
    }

    document.addEventListener('mousemove', handleMouseMoveThrottled)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMoveThrottled)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing, handleMouseMove, handleMouseUp])

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column-reverse',
        overflow: 'hidden',
        borderBottomLeftRadius: '10px',
        borderBottomRightRadius: '10px',
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          width: '100%',
          userSelect: isResizing ? 'none' : 'auto',
          pointerEvents: 'all',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            height: `${panelHeight}px`,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '10px',
          }}
        >
          <Box
            ref={headerRef}
            sx={{
              backgroundColor: '#F2F2F2',
              borderRadius: '10px 10px 0px 0px',
              borderBottom: '1px solid #EFEFEF',
              color: '#04002E',
            }}
          >
            <Box
              onMouseDown={handleMouseDown}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'ns-resize',
                pt: '5px',
              }}
            >
              <Box
                sx={{
                  width: '80px',
                  height: '3px',
                  borderRadius: '4px',
                  background: '#E4E4E4',
                  boxShadow: '0px 1px 1px 0px rgba(135, 135, 135, 0.25) inset',
                }}
              ></Box>
            </Box>
            {header}
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              paddingY: 1,
              paddingLeft: 1,
              overflowY: 'scroll',
              '&::-webkit-scrollbar': {
                width: '12px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '8px',
                backgroundColor: '#BBBCBC',
                backgroundClip: 'padding-box',
                border: '4px solid transparent',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
            }}
          >
            {content}
          </Box>
        </Paper>
      </Box>
    </Box>
  )
}
